<script>
  import Page from '@components/page.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import Button from '@components/buttons/button.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { CirclePlus, Pencil, Trash2, BookOpen, CheckCheck, CalendarCheck, CalendarClock, CalendarX, EyeOff, ExternalLink } from 'lucide-svelte'
  import { router } from '@inertiajs/svelte'
  import { formatDateText } from '@lib/utils/date'
  import { sortItems } from '@lib/utils'

  let { programmes = [], pagination } = $props()

  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let status = usePageParam({ key: "status", defaultValue: "all", items: [
    { value: "all", label: "All programmes", icon: CheckCheck },
    { value: "active", label: "Active", icon: CalendarCheck },
    { value: "upcoming", label: "Upcoming", icon: CalendarClock },
    { value: "past", label: "Past", icon: CalendarX },
    { value: "inactive", label: "Inactive", icon: EyeOff },
  ] })

  function deleteProgramme(programme) {
    if (!window.confirm(`Are you sure you want to delete ${programme.name}?`)) return
    router.delete(`/admin/programmes/${programme.id}`)
  }

  function statusVariant(programme) {
    if (!programme.active) return "default"
    if (programme.current) return "success"
    return "warning"
  }

  function statusLabel(programme) {
    if (!programme.active) return "Inactive"
    if (programme.current) return "Active"
    return "Upcoming"
  }
</script>

<Page title="Programmes" subtitle="Manage your programmes" links={[{ text: "Admin", href: "/admin" }]} Icon={BookOpen}>

  {#snippet buttons()}
    <Button text="New programme" path="/admin/programmes/new" Icon={CirclePlus} />
  {/snippet}

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["programme", "programmes"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label, Icon: sort.icon, variant: "outline", size: "md" }} width="12rem" align="start" itemSize="md">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => sort.setValue(sortItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Status filter -->
      <Dropdown buttonProps={{ text: status.label, Icon: status.icon, variant: "outline", size: "md" }} width="12rem" align="start" itemSize="md">
        {#snippet contents(item)}
          {#each status.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => status.setValue(statusItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

    {/snippet}
  </FilterBar>

  <Table.Root empty={programmes.length === 0} emptyMessage="No programmes yet">
    <Table.Head>
      <Table.Header width="30%">Name</Table.Header>
      <Table.Header width="15%">Starts</Table.Header>
      <Table.Header width="15%">Ends</Table.Header>
      <Table.Header width="10%" align="center">Offers</Table.Header>
      <Table.Header width="10%" align="center">Members</Table.Header>
      <Table.Header width="10%" align="center">Status</Table.Header>
    </Table.Head>

    <Table.Body>
      {#each programmes as programme (programme.id)}
        <Table.Row>
          <Table.Cell width="30%">
            <div class="flex items-center gap-2 group">
              <Button variant="link" class="text-gray-800 font-medium leading-none" path={`/admin/programmes/${programme.id}`}>{programme.name}</Button>
              <Button variant="naked" Icon={ExternalLink} hint="View" path={`/admin/programmes/${programme.id}`} class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} />
              <Button variant="naked" Icon={Pencil} hint="Edit" class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} path={`/admin/programmes/${programme.id}/edit`} />
              <Button variant="naked" Icon={Trash2} hint="Delete" class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} onclick={() => deleteProgramme(programme)} />
            </div>
            {#if programme.key}
              <span class="text-xs text-gray-400">{programme.key}</span>
            {/if}
          </Table.Cell>
          <Table.Cell width="15%">{formatDateText(programme.starts_on)}</Table.Cell>
          <Table.Cell width="15%">{formatDateText(programme.ends_on)}</Table.Cell>
          <Table.Cell width="10%" align="center">{programme.offers_count}</Table.Cell>
          <Table.Cell width="10%" align="center">{programme.memberships_count}</Table.Cell>
          <Table.Cell width="10%" align="center">
            <BoxedText variant={statusVariant(programme)} size="xs" text={statusLabel(programme)} />
          </Table.Cell>
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["programme", "programmes"] }} />

</Page>
